import { useEffect, useRef, useState } from "react";
import { List, ListRow } from "../../components/List";
import { Table } from "../../components/Table";
import { TextLink } from "../../components/TextLink";
import { Badge, Button, ErrorText, inputCls, PageHeader, Panel } from "../../components/ui";
import { api, get, post } from "../../lib/api";
import { realtime } from "../../lib/ws";

/**
 * The voice lab (voice slice 0, the spike; deleted in slice 1): talk to GPT-Live as Pandito and measure it. The
 * session timeline's numbers come from the side channel, through the server; the browser's own come from the audio.
 */

type Lab = { unavailable: string | null; voices: string[]; fidelityLine: string };
type Measures = {
  firstWords: { atMs: number; ms: number; heard: string }[];
  interruptions: { atMs: number; ms: number; said: string }[];
  fidelity: { eventId: string; reference: string; spoken: string; items: { token: string; found: boolean }[]; score: string }[];
  delegations: { id: string; offsetMs: number; words: string }[];
  usage: { seconds: number; ratio: number | null } | null;
};
type LabEvent = { sessionId: string; kind: string; at?: number; event?: any; state?: string; said?: { text: string; kind: string }; measures?: Measures };
type Phase = "idle" | "connecting" | "live" | "ending";

/** Loud means an RMS above this for 30 ms; quiet, below it for 300 ms. The mic's is higher: a room is never silent. */
const MIC_LOUD = 0.02;
const HOST_LOUD = 0.01;

const median = (xs: number[]) => (xs.length ? [...xs].sort((a, b) => a - b)[Math.floor(xs.length / 2)]! : null);
const msLine = (xs: number[]) => (xs.length ? `median ${median(xs)} ms · ${xs.join(", ")}` : "—");

/**
 * The browser's own measures, from the audio: the Host's first sound after the mic went quiet, and how long the Host
 * kept sounding once the mic came in over it. One detector per stream, read every 10 ms.
 */
function createMeter(onLevels: (mic: number, host: number) => void) {
  const ctx = new AudioContext();
  const analysers: { mic?: AnalyserNode; host?: AnalyserNode } = {};
  const firstWords: number[] = [];
  const interruptions: number[] = [];
  const st = {
    mic: { loud: false, run: 0, quiet: 0, lastLoud: 0 },
    host: { loud: false, run: 0, quiet: 0, lastLoud: 0 },
    micEnded: null as number | null,
    cutAt: null as number | null,
  };
  const rms = (a?: AnalyserNode) => {
    if (!a) return 0;
    const buf = new Float32Array(a.fftSize);
    a.getFloatTimeDomainData(buf);
    return Math.sqrt(buf.reduce((n, v) => n + v * v, 0) / buf.length);
  };
  const step = (s: typeof st.mic, level: number, loud: number, now: number) => {
    if (level > loud) {
      s.run++;
      s.quiet = 0;
      s.lastLoud = now;
      if (!s.loud && s.run >= 3) return (s.loud = true), "on";
    } else {
      s.run = 0;
      if (s.loud && ++s.quiet >= 30) return (s.loud = false), "off";
    }
    return null;
  };
  let frames = 0;
  const timer = setInterval(() => {
    const now = performance.now();
    const mic = rms(analysers.mic);
    const host = rms(analysers.host);
    const m = step(st.mic, mic, MIC_LOUD, now);
    const h = step(st.host, host, HOST_LOUD, now);
    if (m === "on" && st.host.loud) st.cutAt = now - 20;
    if (m === "off" && !st.host.loud) st.micEnded = st.mic.lastLoud;
    if (m === "on") st.micEnded = null;
    if (h === "on" && st.micEnded !== null) {
      firstWords.push(Math.round(now - 20 - st.micEnded));
      st.micEnded = null;
    }
    if (h === "off" && st.cutAt !== null) {
      interruptions.push(Math.round(st.host.lastLoud - st.cutAt));
      st.cutAt = null;
    }
    if (++frames % 10 === 0) onLevels(mic, host);
  }, 10);
  const watch = (which: "mic" | "host", stream: MediaStream) => {
    const a = ctx.createAnalyser();
    a.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(a);
    analysers[which] = a;
  };
  return {
    watch,
    read: () => ({ firstWords: [...firstWords], interruptions: [...interruptions] }),
    stop: () => {
      clearInterval(timer);
      void ctx.close();
    },
  };
}

type Call = { pc: RTCPeerConnection; mic: MediaStream | null; audio: HTMLAudioElement; meter: ReturnType<typeof createMeter> };

/** Ends a minted call from the page; the server closes it upstream. */
const endCall = (sessionId: string, browser?: unknown) => api("/api/voice/lab/session", { method: "DELETE", body: { sessionId, browser } });

export function AdminVoiceLab() {
  const [lab, setLab] = useState<Lab | null>(null);
  const [voice, setVoice] = useState("marin");
  const [phase, setPhase] = useState<Phase>("idle");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [events, setEvents] = useState<LabEvent[]>([]);
  const [browserLog, setBrowserLog] = useState<string[]>([]);
  const [levels, setLevels] = useState({ mic: 0, host: 0 });
  const [browser, setBrowser] = useState({ firstWords: [] as number[], interruptions: [] as number[] });
  const [line, setLine] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [download, setDownload] = useState<string | null>(null);
  const call = useRef<Call | null>(null);
  const current = useRef<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    get<Lab>("/api/voice/lab/session").then(setLab, (e) => setError(e.message));
    const off = realtime.subscribe("admin", (e) => {
      if (e.type === "voice.lab") setEvents((all) => [...all, e.payload as LabEvent].slice(-4000));
    });
    return () => {
      mounted.current = false;
      off();
      hangUp();
      if (current.current) void endCall(current.current).catch(() => {});
    };
  }, []);
  useEffect(() => {
    if (phase !== "live") return;
    const t = setInterval(() => call.current && setBrowser(call.current.meter.read()), 1000);
    return () => clearInterval(t);
  }, [phase]);

  const mine = events.filter((e) => e.sessionId === sessionId);
  const measures = [...mine].reverse().find((e) => e.measures)?.measures ?? null;
  const side = [...mine].reverse().find((e) => e.kind === "side_channel")?.state ?? (sessionId ? "connecting" : null);
  const lastDelegation = measures?.delegations.at(-1) ?? null;
  const note = (text: string) => setBrowserLog((l) => [`${new Date().toISOString().slice(11, 23)} ${text}`, ...l].slice(0, 200));

  function hangUp() {
    const c = call.current;
    call.current = null;
    if (!c) return;
    c.meter.stop();
    c.mic?.getTracks().forEach((t) => t.stop());
    c.pc.close();
    c.audio.srcObject = null;
    setLevels({ mic: 0, host: 0 });
  }

  async function talk() {
    setError(null);
    setDownload(null);
    setPhase("connecting");
    let minted: string | null = null;
    // Leaving the page mid-setup tears it down at the next step: nothing is left listening or billing.
    const stillHere = () => {
      if (!mounted.current) throw new Error("left the page");
    };
    try {
      const pc = new RTCPeerConnection();
      const audio = new Audio();
      audio.autoplay = true;
      const meter = createMeter((mic, host) => setLevels({ mic, host }));
      call.current = { pc, mic: null, audio, meter };
      const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      call.current.mic = mic;
      stillHere();
      pc.ontrack = (e) => {
        const stream = new MediaStream([e.track]);
        audio.srcObject = stream;
        audio.play().catch(() => note("the browser blocked playback: click anywhere on the page"));
        meter.watch("host", stream);
      };
      mic.getAudioTracks().forEach((t) => pc.addTrack(t, mic));
      meter.watch("mic", mic);
      // The browser may only listen: the lock is tested on every call with one append the session must refuse.
      const channel = pc.createDataChannel("oai-events");
      channel.onmessage = (m) => {
        const e = JSON.parse(m.data);
        if (e.type === "session.started") {
          note(`data channel: session.started ${e.session?.id ?? ""}`);
          channel.send(JSON.stringify({ type: "session.thinking.append", event_id: "lab_browser_probe", delegation_id: null, content: "Lab probe from the browser: ignore this." }));
        } else if (e.type === "error" || e.type === "info" || e.client_event_id === "lab_browser_probe") {
          note(`data channel: ${e.type} ${e.code ?? e.error?.code ?? ""} ${e.message ?? e.error?.message ?? ""}`);
        }
      };
      await pc.setLocalDescription(await pc.createOffer());
      await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === "complete") return resolve();
        pc.addEventListener("icegatheringstatechange", () => pc.iceGatheringState === "complete" && resolve());
        setTimeout(resolve, 3000);
      });
      stillHere();
      const answer = await post<{ sessionId: string; sdp: string }>("/api/voice/lab/session", { sdp: pc.localDescription!.sdp, voice });
      minted = answer.sessionId;
      stillHere();
      current.current = minted;
      setSessionId(minted);
      await pc.setRemoteDescription({ type: "answer", sdp: answer.sdp });
      stillHere();
      setPhase("live");
    } catch (e: any) {
      hangUp();
      if (minted) void endCall(minted).catch(() => {});
      current.current = null;
      if (!mounted.current) return;
      setPhase("idle");
      setError(e?.message ?? String(e));
    }
  }

  async function end() {
    if (!sessionId) return;
    setPhase("ending");
    const measured = call.current?.meter.read() ?? browser;
    setBrowser(measured);
    try {
      const record = await endCall(sessionId, measured);
      setDownload(URL.createObjectURL(new Blob([JSON.stringify({ ...record, browserLog }, null, 2)], { type: "application/json" })));
    } catch (e: any) {
      setError(e?.message ?? String(e));
    }
    current.current = null;
    hangUp();
    setPhase("idle");
  }

  async function say(kind: "line" | "fidelity", delegationId: string | null = null) {
    setError(null);
    const text = kind === "fidelity" ? lab!.fidelityLine : line;
    try {
      await post("/api/voice/lab/say", { sessionId, text, kind, delegationId });
      if (kind === "line") setLine("");
    } catch (e: any) {
      setError(e?.message ?? String(e));
    }
  }

  const live = phase === "live";
  return (
    <div>
      <PageHeader
        title="Voice lab"
        subtitle="Talk to GPT-Live as Pandito and measure it: first word, interruption, fidelity, delegation. A call costs $0.05 a minute; it is deleted with voice slice 1."
        actions={
          live || phase === "ending" ? (
            <Button variant="danger" disabled={phase === "ending"} onClick={end}>{phase === "ending" ? "Ending…" : "End"}</Button>
          ) : (
            <Button variant="primary" disabled={!lab || Boolean(lab.unavailable) || phase === "connecting"} onClick={talk}>{phase === "connecting" ? "Connecting…" : "Talk"}</Button>
          )
        }
      />
      <div className="space-y-6 px-4 py-4 md:px-6">
        {lab?.unavailable && <Panel title="Not set up">{lab.unavailable}</Panel>}
        <ErrorText error={error} />
        <Panel title="Call" hint="Use headphones: the mic must not hear the Host, or every answer looks like an interruption.">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <select aria-label="Voice" className={`${inputCls} w-40`} value={voice} disabled={phase !== "idle"} onChange={(e) => setVoice(e.target.value)}>
              {(lab?.voices ?? ["marin"]).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <Badge tone={live ? "emerald" : "zinc"}>{phase}</Badge>
            {side && <Badge tone={side === "open" ? "blue" : "amber"}>side channel {side}</Badge>}
            {sessionId && <span className="font-mono text-xs text-faint">{sessionId}</span>}
          </div>
          <div className="mt-4 grid gap-2 text-xs text-muted sm:grid-cols-2">
            {(["mic", "host"] as const).map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-10">{k === "mic" ? "You" : "Host"}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-fill">
                  <div className="h-full rounded-full bg-success transition-[width] duration-100" style={{ width: `${Math.min(100, levels[k] * 400)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Hand the Host a line" hint="A commentary append: what the Host should say, which it may paraphrase.">
          <textarea className={inputCls} rows={2} value={line} onChange={(e) => setLine(e.target.value)} placeholder="A line for the Host to say" />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={!live || !line.trim()} onClick={() => say("line")}>Say it</Button>
            <Button size="sm" disabled={!live || !line.trim() || !lastDelegation} onClick={() => say("line", lastDelegation!.id)}>Answer the last delegation</Button>
            <Button size="sm" disabled={!live || !lab} onClick={() => say("fidelity")}>Fidelity line</Button>
          </div>
          {lab && <p className="mt-2 text-xs text-faint">Fidelity line: {lab.fidelityLine}</p>}
        </Panel>

        <Table
          title="Measures"
          hint="Session timeline from the side channel's transcript and audio times; browser from the audio the page heard."
          rows={[
            { what: "First word after you stop", timeline: msLine(measures?.firstWords.map((f) => f.ms) ?? []), browser: msLine(browser.firstWords) },
            { what: "Host stops when you cut in", timeline: msLine(measures?.interruptions.map((i) => i.ms) ?? []), browser: msLine(browser.interruptions) },
            { what: "Usage", timeline: measures?.usage ? `${measures.usage.seconds} s · context ${measures.usage.ratio ?? "—"}` : "—", browser: "" },
          ]}
          rowKey={(r) => r.what}
          columns={[
            { header: "Measure", primary: true, cell: (r) => r.what },
            { header: "Session timeline", className: "text-xs text-muted", cell: (r) => r.timeline },
            { header: "Browser", className: "text-xs text-muted", cell: (r) => r.browser },
          ]}
        />

        <Table
          title="Fidelity"
          hint="Every number and name of the line, and whether the Host's transcript kept it."
          empty="Press Fidelity line during a call, five times."
          rows={measures?.fidelity ?? []}
          rowKey={(f) => f.eventId}
          columns={[
            { header: "Spoken", primary: true, cell: (f) => f.spoken || <span className="text-faint">waiting for the Host…</span> },
            { header: "Kept", cell: (f) => <Badge tone={f.items.every((i) => i.found) ? "emerald" : "red"}>{f.score}</Badge> },
            { header: "Lost", className: "text-xs text-danger", cell: (f) => f.items.filter((i) => !i.found).map((i) => i.token).join(", ") },
          ]}
        />

        <List title="Delegations" hint="What the assembler would hand Pandito: your words around the signal's offset." empty={'Say "ask Pandito what time it is in Athens".'}>
          {(measures?.delegations ?? []).map((d) => (
            <ListRow key={d.id} title={d.words || <span className="text-faint">no words heard before it</span>} subtitle={`${d.id} · at ${d.offsetMs} ms`} />
          ))}
        </List>

        <List
          title="Log"
          hint="Everything the side channel received, newest first; audio is counted, not shown."
          scroll="tall"
          empty="Nothing yet."
          action={download && <TextLink href={download} download={`voice-lab-${sessionId ?? "call"}.json`}>Download the call</TextLink>}
        >
          {[...browserLog.map((b, i) => ({ key: `b${browserLog.length - i}`, title: b, sub: "browser" })), ...[...mine].reverse().map((e, i) => ({ key: `s${i}`, ...describe(e) }))].map((r) => (
            <ListRow key={r.key} title={<span className="font-mono text-xs">{r.title}</span>} subtitle={r.sub} />
          ))}
        </List>
      </div>
    </div>
  );
}

/** One line of the log for an event the server forwarded. */
function describe(e: LabEvent): { title: string; sub: string } {
  const at = e.at !== undefined ? `+${(e.at / 1000).toFixed(2)} s` : "";
  if (e.kind === "event") {
    const ev = e.event ?? {};
    const detail = ev.delta ?? ev.error?.message ?? ev.message ?? (ev.delegation ? `${ev.delegation.id} at ${ev.offset_ms} ms` : "");
    const times = Number.isFinite(ev.start_ms) ? ` [${ev.start_ms}–${ev.end_ms}]` : "";
    return { title: `${ev.type}${times} ${typeof detail === "string" ? detail : ""}`.trim(), sub: `${at} · ${ev.usage ? `${ev.usage.seconds} s` : ev.client_event_id ?? ""}` };
  }
  if (e.kind === "said") return { title: `said (${e.said?.kind}): ${e.said?.text}`, sub: at };
  if (e.kind === "side_channel") return { title: `side channel ${e.state}`, sub: at };
  return { title: e.kind, sub: at };
}
